import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subWeeks, subMonths } from "date-fns";

const stripAccents = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const fmt = (date) => format(date, "yyyy-MM-dd");

// Atalhos de data por palavra-chave - sem lib de NLP, so' um dicionario
// fixo. Se faltar algum atalho que o time queira, e' rapido de estender
// aqui (so' adicionar uma entrada nova).
const DATE_PATTERNS = [
  {
    regex: /\bhoje\b/,
    compute: () => {
      const d = new Date();
      return { dateStart: fmt(d), dateEnd: fmt(d) };
    },
    label: () => "Hoje",
  },
  {
    regex: /\bontem\b/,
    compute: () => {
      const d = subDays(new Date(), 1);
      return { dateStart: fmt(d), dateEnd: fmt(d) };
    },
    label: () => "Ontem",
  },
  {
    regex: /\b(essa|esta) semana\b/,
    compute: () => {
      const now = new Date();
      return { dateStart: fmt(startOfWeek(now)), dateEnd: fmt(now) };
    },
    label: () => "Essa semana",
  },
  {
    regex: /\bsemana passada\b/,
    compute: () => {
      const lastWeek = subWeeks(new Date(), 1);
      return { dateStart: fmt(startOfWeek(lastWeek)), dateEnd: fmt(endOfWeek(lastWeek)) };
    },
    label: () => "Semana passada",
  },
  {
    regex: /\b(esse|este) mes\b/,
    compute: () => {
      const now = new Date();
      return { dateStart: fmt(startOfMonth(now)), dateEnd: fmt(now) };
    },
    label: () => "Esse mês",
  },
  {
    regex: /\bmes passado\b/,
    compute: () => {
      const lastMonth = subMonths(new Date(), 1);
      return { dateStart: fmt(startOfMonth(lastMonth)), dateEnd: fmt(endOfMonth(lastMonth)) };
    },
    label: () => "Mês passado",
  },
  {
    regex: /\bultimos? (\d+) dias\b/,
    compute: (match) => {
      const n = parseInt(match[1], 10);
      const now = new Date();
      return { dateStart: fmt(subDays(now, Math.max(n - 1, 0))), dateEnd: fmt(now) };
    },
    label: (match) => `Últimos ${match[1]} dias`,
  },
];

const STATUS_PATTERNS = [
  { regex: /\babert[oa]s?\b/, status: "open", label: "Abertos" },
  { regex: /\b(pendentes?|aguardando)\b/, status: "pending", label: "Pendentes" },
  { regex: /\b(fechad[oa]s?|encerrad[oa]s?|resolvid[oa]s?)\b/, status: "closed", label: "Fechados" },
  { regex: /\bgrupos?\b/, status: "group", label: "Grupos" },
];

// Interpreta o texto livre digitado na busca e devolve os mesmos filtros
// que os paineis manuais (Tags/Whatsapp/Status/periodo) ja aplicam hoje -
// so' que preenchidos automaticamente a partir do que foi reconhecido no
// texto. O que sobra depois de tirar tudo que foi reconhecido vira
// searchParam (nome do contato / conteudo da mensagem).
export function parseSmartSearch(rawText, { tags = [], whatsapps = [], queues = [] } = {}) {
  let text = stripAccents(rawText || "");
  const chips = [];
  let dateStart;
  let dateEnd;
  const statusFilter = [];
  const tagIds = [];
  const whatsappIds = [];
  const queueIds = [];

  for (const pattern of DATE_PATTERNS) {
    const match = text.match(pattern.regex);
    if (match) {
      const computed = pattern.compute(match);
      dateStart = computed.dateStart;
      dateEnd = computed.dateEnd;
      chips.push({ type: "date", label: `📅 ${pattern.label(match)}`, value: "date" });
      text = text.replace(pattern.regex, " ");
      break; // so' um periodo por busca
    }
  }

  STATUS_PATTERNS.forEach((pattern) => {
    const match = text.match(pattern.regex);
    if (match) {
      statusFilter.push(pattern.status);
      chips.push({ type: "status", label: `🏷️ ${pattern.label}`, value: pattern.status });
      text = text.replace(pattern.regex, " ");
    }
  });

  // Setor/fila (Comercial, Suporte, Financeiro etc.) - checado antes das
  // tags porque "setor" e' o que o time chama esses nomes no dia a dia,
  // e nao existem como Tags no sistema (sao um cadastro separado, Queue).
  queues.forEach((queue) => {
    const normalized = stripAccents(queue.name);
    if (normalized.length >= 3 && text.includes(normalized)) {
      queueIds.push(queue.id);
      chips.push({ type: "queue", label: `📁 ${queue.name}`, value: queue.id });
      text = text.split(normalized).join(" ");
    }
  });

  tags.forEach((tag) => {
    const normalized = stripAccents(tag.name);
    if (normalized.length >= 3 && text.includes(normalized)) {
      tagIds.push(tag.id);
      chips.push({ type: "tag", label: `🏷️ ${tag.name}`, value: tag.id });
      text = text.split(normalized).join(" ");
    }
  });

  whatsapps.forEach((w) => {
    const normalized = stripAccents(w.name);
    if (normalized.length >= 3 && text.includes(normalized)) {
      whatsappIds.push(w.id);
      chips.push({ type: "whatsapp", label: `📱 ${w.name}`, value: w.id });
      text = text.split(normalized).join(" ");
    }
  });

  const searchParam = text.replace(/\s+/g, " ").trim();

  return { searchParam, dateStart, dateEnd, tagIds, whatsappIds, queueIds, statusFilter, chips };
}
